import { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { Button, FormErrorMessage, Input } from '@dashboard/ui'
import { CompanyGame } from '@/api/dashboard'
import { FieldGroup, ToastSeverity, useToast } from '@/ui'
import { Plus, Trash } from '@/icons'
import { domainRegexp } from '@/libs'
import { FormBlock } from '@/components/ui/FormBlock/FormBlock'
import { FormLayout } from '../../company/components/FormLayout'
import { addCompanyToCache, useCompaniesQuery } from '@/api/useCompaniesQuery'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { dashboardClient, getErrorText } from '@/api'
import { CompanySettingsFormSkeleton } from '../../company/components/CompanySettingsFormSkeleton'
import { useCurrentUser } from '@/api/useCurrentUser'

export const UsersDomainsForm = () => {
  const { canEdit } = useCurrentUser()
  const queryClient = useQueryClient()
  const showToast = useToast()
  const { companyId } = useParams() as { companyId: string }
  const { data: dataCompanies, isLoading } = useCompaniesQuery()
  const company = useMemo(() => dataCompanies?.data?.find(c => c.id === companyId), [dataCompanies, companyId])
  const { t } = useTranslation()

  const {
    formState: { isSubmitting, isDirty, errors },
    handleSubmit,
    register,
    unregister,
    setValue,
    reset,
    watch,
  } = useForm<CompanyGame>({
    defaultValues: {
      domains: [],
      viewer_domains: [],
      ...company,
    },
  })

  const save = useCallback(
    async (data: CompanyGame) => {
      try {
        await dashboardClient.v1.updateCompany(companyId, data)
        addCompanyToCache(queryClient, {
          ...data,
        })
        showToast({ message: t('saved'), severity: ToastSeverity.success })
      } catch (e) {
        showToast({ message: getErrorText(e), severity: ToastSeverity.error })
      }
    },
    [showToast],
  )

  if (isLoading || !company) {
    return <CompanySettingsFormSkeleton />
  }

  const onSubmit = handleSubmit(async data => {
    try {
      const clearData = {
        ...data,
        domains: data.domains?.filter(Boolean),
        viewer_domains: data.viewer_domains?.filter(Boolean),
      }
      await save(clearData)
      reset(clearData)
    } catch (e) {
      console.log(e)
    }
  })

  const onClickDiscard = () => {
    reset(company)
  }

  const renderDomainBlock = (key: 'viewer_domains' | 'domains') => {
    const domains = watch(key) || []

    const onClickAddDomain = () => {
      setValue(key, [...domains, ''], { shouldDirty: true })
    }

    const onClickDeleteDomain = (index: number) => {
      unregister(`${key}.${index}`)
      setValue(
        key,
        domains.filter((_, i) => i !== index),
        { shouldDirty: true },
      )
    }

    return (
      <div className="rounded-lg bg-fg-secondary p-6">
        {!!domains.length && (
          <FieldGroup>
            {domains.map((_, index) => (
              <div key={`domains-${index}`} className="mb-2 flex gap-1.5 last:mb-0">
                <div className="w-full">
                  <Input
                    {...register(`${key}.${index}`, {
                      required: t('validation.required'),
                      pattern: { value: domainRegexp, message: t('validation.invalid_url') },
                    })}
                    disabled={!canEdit}
                    placeholder="example.com"
                  />
                  <ErrorMessage
                    name={`${key}.${index}`}
                    errors={errors}
                    render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
                  />
                </div>
                {canEdit && (
                  <Button
                    className="h-9"
                    variant="outline"
                    color="secondary"
                    type="button"
                    onClick={() => onClickDeleteDomain(index)}
                  >
                    <Trash size={18} />
                  </Button>
                )}
              </div>
            ))}
          </FieldGroup>
        )}

        {canEdit && (
          <FieldGroup>
            <Button variant="outline" color="secondary" size="sm" type="button" onClick={onClickAddDomain}>
              <Plus size={18} />
              <span>{t('company-settings-page.add-domain')}</span>
            </Button>
          </FieldGroup>
        )}
      </div>
    )
  }

  return (
    <FormLayout
      footer={
        <>
          {isDirty && (
            <Button variant="outline" color="secondary" size="sm" onClick={onClickDiscard}>
              {t('Discard')}
            </Button>
          )}
          <Button size="sm" loading={isSubmitting} disabled={!isDirty} type="submit">
            {t('Save')}
          </Button>
        </>
      }
      onSubmit={onSubmit}
    >
      <FormBlock
        title={t('company-settings-page.domains')}
        description={<Trans i18nKey="company-settings-page.domains.desc" components={{ b: <b /> }} />}
      >
        {renderDomainBlock('domains')}
      </FormBlock>

      <FormBlock
        title={t('company-settings-page.viewer_domains')}
        description={<Trans i18nKey="company-settings-page.viewer_domains.desc" components={{ b: <b /> }} />}
      >
        {renderDomainBlock('viewer_domains')}
      </FormBlock>
    </FormLayout>
  )
}
